/**
 * Service layer — print orders to reprographics shops (Supabase `print_orders`, via
 * /api/print-orders). A shop is an Organization whose Type includes "Reprographics".
 * Components call only these functions. See docs/PRINT_ORDERS.md.
 */

export const REPRO_ORG_TYPE = 'Reprographics'

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/print-orders' + (qs ? '?' + qs : ''), {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  const b = await res.json().catch(() => ({}))
  if (!res.ok) { const e = new Error(b.error || 'Print order request failed: ' + res.status); e.order = b.order; throw e }
  return b
}

/** The Organizations (from services/team.js#getOrganizations) that are print shops, A to Z. */
export function reproShops(organizations) {
  return (organizations || []).filter(o => {
    const t = o.fields && o.fields['Type']
    const arr = Array.isArray(t) ? t : (t ? [t] : [])
    return arr.includes(REPRO_ORG_TYPE)
  }).sort((a, b) => String(a.fields['Organization Name'] || '').localeCompare(String(b.fields['Organization Name'] || '')))
}

/** A project's print orders (newest first), every shop's cities served, and the project's city.
 *  → { orders, shopCities: { orgId: [city] }, projectCity } */
export function getProjectPrintOrders(projectId) {
  if (!projectId) return Promise.resolve({ orders: [], shopCities: {}, projectCity: '' })
  return call('GET', null, 'projectId=' + encodeURIComponent(projectId))
    .then(d => ({ orders: d.orders || [], shopCities: d.shopCities || {}, projectCity: d.projectCity || '' }))
}

/** The cities one shop serves (for the Contacts organization window). */
export function getShopCities(orgAirtableId) {
  return call('GET', null, 'action=shopCities&orgId=' + encodeURIComponent(orgAirtableId)).then(d => d.cities || [])
}

/** Save the cities a shop serves (an empty list = shows on every project). */
export function setShopCities(orgAirtableId, cities) {
  return call('POST', { action: 'setShopCities', orgAirtableId, cities }).then(d => d.cities || [])
}

/** Organization types whose coverage is by county (database/391) — surveyors and as-built firms. */
export const COUNTY_COVERAGE_ORG_TYPES = ['Surveyor', 'As-Built']

/** A surveyor's / as-built firm's coverage → { counties: [...], nationwide } */
export function getProviderCoverage(orgAirtableId) {
  return call('GET', null, 'action=shopCities&orgId=' + encodeURIComponent(orgAirtableId))
    .then(d => ({ counties: d.counties || [], nationwide: !!d.nationwide }))
}

/** Save the counties a provider serves, or nationwide. Nothing listed and not nationwide = offered nowhere. */
export function setProviderCoverage(orgAirtableId, counties, nationwide) {
  return call('POST', { action: 'setShopCities', orgAirtableId, counties, nationwide })
    .then(d => ({ counties: d.counties || [], nationwide: !!d.nationwide }))
}

/** "Laguna Beach, Dana Point" or one per line → ['Laguna Beach', 'Dana Point'] */
export function parseCities(text) {
  return String(text || '').split(/[,;\n]+/).map(c => c.trim()).filter(Boolean)
}

const norm = c => String(c || '').trim().toLowerCase().replace(/,.*$/, '').replace(/\s+/g, ' ')

/**
 * Which shops serve this project. A shop with cities listed serves those cities plus its own
 * (its Contacts address city); a shop with none listed serves everywhere. With no project city
 * every shop is local. → { local: [...orgs], other: [...orgs] }
 */
export function splitShopsByCity(shops, shopCities, projectCity) {
  const here = norm(projectCity)
  const local = [], other = []
  for (const org of shops || []) {
    const listed = (shopCities && shopCities[org.id]) || []
    if (!here || !listed.length) { local.push(org); continue }
    const served = new Set([...listed, org.fields && org.fields['Client City']].filter(Boolean).map(norm))
    ;(served.has(here) ? local : other).push(org)
  }
  return { local, other }
}

/** { project:{name,number}, drawings:[{number,title,included}], office } */
export function getPrintOrderPrep(projectId) {
  return call('GET', null, 'action=prep&projectId=' + encodeURIComponent(projectId))
}

/** Upload one file for an order; resolves { url, name, bytes }. */
export async function uploadPrintFile(projectId, file) {
  const { uploadUrl, publicUrl } = await call('POST', { action: 'uploadUrl', projectId, filename: file.name })
  const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file })
  if (!put.ok) throw new Error('Upload failed for ' + file.name + ' (' + put.status + ')')
  return { url: publicUrl, name: file.name, bytes: file.size }
}

export function previewPrintOrder(order) {
  return call('POST', { action: 'previewOrder', ...order }).then(d => d.email)
}

export function sendPrintOrder(order) {
  return call('POST', { action: 'sendOrder', ...order }).then(d => d.order)
}

export function setPrintOrderStatus(orderId, status, note) {
  return call('POST', { action: 'setStatus', orderId, status, note: note || null }).then(d => d.order)
}

export function deletePrintOrder(orderId) {
  return call('POST', { action: 'deleteOrder', orderId })
}
